import React from 'react'
import { observer } from 'mobx-react'
import { Link } from 'react-router-dom'
import {
    Drawer,
    Grid,
    Icon,
    Pagination,
    Dropdown,
} from '@softwaredev_cegeka/stix-core'
import { authenticatedUserStore, notifierStore } from 'src/stores'

import { UserTable } from 'src/components/EntityTable'
import { AuthApi } from 'src/api/authApi'
import UserDrawerContent from 'src/components/Drawer/UserDrawerContent'
import DrawerHeader from '../components/Drawer/DrawerHeader'
import { debouncedLoadData } from 'src/utils/utils'

class UserRoute extends React.Component {
    state = {
        users: [],
        selectedUser: null,
        page: 1,
        totalPages: 1,
        totalElements: 1,
        loading: false,
        params: {},
        toastInfo: {
            message: '',
            didSucceed: true,
        },
    }

    authClient = new AuthApi(authenticatedUserStore.authToken)

    componentDidMount() {
        this.fetchAndStoreUsers()
    }

    // Cancel still ongoing get requests, to prevent updating state and changing the page to the previous clicked link
    componentWillUnmount() {
        this.authClient.cancelRequest()
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (
            prevProps.selectedOrganization !== this.props.selectedOrganization
        ) {
            this.fetchAndStoreUsers(1)
        }
    }

    fetchAndStoreUsers = (page = this.state.page) => {
        this.setState({ loading: true })
        this.authClient
            .fetchUsers(
                page,
                this.props.history,
                this.state.params,
                authenticatedUserStore.selectedOrganization?.id
            )
            .then(response => {
                this.setState({
                    users: response?.data?.results,
                    totalPages: response?.data?._page.total_pages,
                    totalElements: response?.data?._page.total_elements,
                    loading: false,
                    page: page,
                })
            })
            .catch(err => {
                console.error(err)
                this.setState({
                    loading: false,
                })
            })
    }

    rowSelectHandler = async (selectObj, setDrawerState) => {
        const { selectedRow } = selectObj

        this.setState(
            {
                selectedUser: selectedRow.original.id,
            },
            () => setDrawerState(true)
        )
    }

    handlePageChange = page => {
        this.setState({ page: page }, () => this.fetchAndStoreUsers())
    }

    unlinkUserFromGroup = groupId => {
        this.authClient
            .removeUserFromGroup(this.state.selectedUser, groupId)
            .then(response => {
                if (response.status === 200 || response.status === 204) {
                    this.fetchAndStoreUsers()
                    notifierStore.setToastInfo(
                        'Membership successfully updated',
                        true
                    )
                } else {
                    notifierStore.setToastInfo(
                        'Failed to link the user to the selected group',
                        false
                    )
                }
                notifierStore.setShowMessage(true)
            })
    }

    unlinkUserFromTenant = tenantId => {
        this.authClient
            .removeUserFromTenant(this.state.selectedUser, tenantId)
            .then(response => {
                if (response.status === 200 || response.status === 204) {
                    this.fetchAndStoreUsers()
                    notifierStore.setToastInfo(
                        'User successfully unlinked from tenant',
                        true
                    )
                } else {
                    notifierStore.setToastInfo(
                        'Failed to unlink user from the tenant',
                        false
                    )
                }
            })
            .catch(err => {
                notifierStore.setToastInfo(err.response?.data?.error, false)
            })
            .finally(notifierStore.setShowMessage(true))
    }

    setParams = params => {
        if (this.state.loading) {
            this.authClient.cancelRequest()
            this.authClient = new AuthApi(authenticatedUserStore.authToken)
        }
        this.setState(
            {
                params: params,
            },
            debouncedLoadData(params, () => this.fetchAndStoreUsers(1))
        )
    }

    addUserToGroup = groupId => {
        this.authClient
            .addUserToGroup(this.state.selectedUser, groupId)
            .then(response => {
                if (response.status === 200 || response.status === 204) {
                    this.fetchAndStoreUsers()
                    notifierStore.setToastInfo(
                        'Membership successfully updated',
                        true
                    )
                } else {
                    notifierStore.setToastInfo(
                        'Failed to link the user to the selected group',
                        false
                    )
                }
                notifierStore.setShowMessage(true)
            })
    }

    addUserToTenant = tenantId => {
        this.authClient
            .addUserToTenant(this.state.selectedUser, tenantId)
            .then(response => {
                if (response.status === 200 || response.status === 204) {
                    this.fetchAndStoreUsers()
                    notifierStore.setToastInfo(
                        'Membership successfully updated',
                        true
                    )
                } else {
                    notifierStore.setToastInfo(
                        'Failed to link the user to the selected tenant',
                        false
                    )
                }
            })
            .catch(err => {
                notifierStore.setToastInfo(err.response?.data?.error, false)
            })
            .finally(() => {
                notifierStore.setShowMessage(true)
            })
    }

    removeUser = username => {
        this.authClient
            .deleteUser(username)
            .then(response => {
                if (response.status === 200 || response.status === 204) {
                    this.fetchAndStoreUsers()
                    notifierStore.setToastInfo(
                        'User successfully deleted',
                        true
                    )
                } else {
                    notifierStore.setToastInfo(
                        'Failed to remove user ' + username,
                        false
                    )
                }
            })
            .catch(err => {
                notifierStore.setToastInfo(err.response?.data?.error, false)
            })
        notifierStore.setShowMessage(true)
    }

    getCurrentUser() {
        return this.state.users.find(user => {
            return user.id === this.state.selectedUser
        })
    }

    renderActions() {
        return (
            <Dropdown>
                <Dropdown.Toggle
                    variant="secondary"
                    id="product-templates-actions"
                >
                    <Icon right icon="chevron-down" title="Actions" />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    <Dropdown.Item as={Link} to={{ pathname: '/user-form' }}>
                        <Icon icon="add" title={'Create user'} />
                    </Dropdown.Item>
                    <Dropdown.Item
                        as={Link}
                        to={{ pathname: '/bulk-import-users' }}
                    >
                        <Icon icon="reports" title={'Import users'} />
                    </Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>
        )
    }

    render() {
        const selectedUser = this.getCurrentUser() || {}
        return (
            <div className="entity-drawer">
                <Drawer
                    type="fixed"
                    panelTitle={
                        <DrawerHeader
                            path={`/user-form/${selectedUser.username}`}
                            title={`${selectedUser.first_name} ${selectedUser.last_name}`}
                        />
                    }
                    panelContent={UserDrawerContent(
                        selectedUser,
                        this.addUserToGroup,
                        this.unlinkUserFromGroup,
                        this.addUserToTenant,
                        this.unlinkUserFromTenant
                    )}
                >
                    <Grid.Container>
                        <div className={'page-header'}>
                            <h1 className={'content-title'}>Users overview</h1>
                            {this.renderActions()}
                        </div>

                        <Drawer.Context.Consumer>
                            {({ setDrawerState }) => (
                                <div className="data-table-container">
                                    <UserTable
                                        key={'user-table'}
                                        data={this.state.users}
                                        loading={this.state.loading}
                                        params={this.state.params}
                                        setParams={this.setParams}
                                        rowSelectHandler={selectObj =>
                                            this.rowSelectHandler(
                                                selectObj,
                                                setDrawerState
                                            )
                                        }
                                        deleteAction={username =>
                                            this.removeUser(username)
                                        }
                                    />
                                    {this.state.totalPages >= 2 && (
                                        <div className="pagination-wrapper">
                                            <Pagination
                                                lastPage={this.state.totalPages}
                                                currentPage={this.state.page}
                                                onClick={
                                                    this.state.loading
                                                        ? () => {}
                                                        : this.handlePageChange
                                                }
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </Drawer.Context.Consumer>
                    </Grid.Container>
                </Drawer>
            </div>
        )
    }
}

export default observer(UserRoute)
